"use client";
import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import { AlertTriangle, TrendingDown, TrendingUp, Wrench } from "lucide-react";
import { useNavigate } from "react-router-dom";
import DashboardFilters from "../../Component/dashboard-filters";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "../../Component/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../Component/ui/chart";
import "./allSection.css";
import axios from "axios";
import API_URL from "../../../src/Config";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="newdashboard-tooltip">
        <p className="newdashboard-tooltip-label">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export function BreakdownMaintenanceSection() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [summary, setSummary] = useState({
    total_breakdowns: { count: 0, label: "" },
    this_month: { count: 0, percentage_change: 0, trend: "neutral" },
    most_affected_department: { count: 0, label: "", name: "" },
    second_highest_department: { count: 0, label: "", name: "" },
  });

  const [charts, setCharts] = useState({
    monthly_breakdown_trend: [],
    department_wise_breakdown: [],
    monthly_department_breakdown: [],
  });

  // ✅ Centralized filter state
  const [filters, setFilters] = useState({
    department_id: "",
    date_preset: "all",
    showCustomDate: false,
    start_date: "",
    end_date: "",
  });

  const fetchBreakdownData = async (currentFilters = filters) => {
    try {
      setLoading(true);

      const authKey = localStorage.getItem("authKey");
      const params = new URLSearchParams();

      if (currentFilters.date_preset)
        params.append("filter_option", currentFilters.date_preset);

      if (currentFilters.department_id)
        params.append("department_id", currentFilters.department_id);

      if (currentFilters.start_date)
        params.append("start_date", currentFilters.start_date);

      if (currentFilters.end_date)
        params.append("end_date", currentFilters.end_date);

      const response = await axios.get(
        `${API_URL}/api/kpi/breakdown-maintenance?${params.toString()}`,
        { headers: { Authorization: authKey } },
      );

      if (response.status === 200) {
        setSummary(response.data.summary || {});
        setCharts(response.data.charts || {});
      }
    } catch (err) {
      console.error("Error fetching Breakdown KPIs:", err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Apply filters
  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    fetchBreakdownData(newFilters);
  };

  // ✅ Reset filters
  const handleResetFilters = () => {
    const reset = {
      department_id: "",
      date_preset: "all",
      showCustomDate: false,
      start_date: "",
      end_date: "",
    };
    setFilters(reset);
    fetchBreakdownData(reset);
  };

  useEffect(() => {
    fetchBreakdownData();
  }, []);

  // Card click handler
  const handleCardClick = (listType) => {
    const params = new URLSearchParams();
    params.append("list_type", listType);

    const filterOptionValue = filters.date_preset ? filters.date_preset : "All";
    params.append("filter_option", filterOptionValue);

    if (filters.department_id && filters.department_id.trim() !== "") {
      params.append("department_id", filters.department_id);
    }

    if (
      filters.start_date &&
      filters.end_date &&
      filters.start_date.trim() !== "" &&
      filters.end_date.trim() !== ""
    ) {
      params.append("start_date", filters.start_date);
      params.append("end_date", filters.end_date);
    }

    console.log("Navigating to Breakdown List with params:", params.toString());

    window.open(`/breakdown-list?${params.toString()}`, "_blank");
    // navigate(`/breakdown-list?${params.toString()}`);
  };

  // Helper function to get trend icon and color
  const getTrendDisplay = () => {
    const { percentage_change, trend } = summary.this_month;
    const absChange = Math.abs(percentage_change);

    if (trend === "increase") {
      return {
        icon: (
          <TrendingUp
            className="newdashboard-card-icon"
            style={{ color: "#ef4444" }}
          />
        ),
        text: `${absChange}% increase from last month`,
        color: "#ef4444",
      };
    } else if (trend === "decrease") {
      return {
        icon: (
          <TrendingDown
            className="newdashboard-card-icon"
            style={{ color: "#10b981" }}
          />
        ),
        text: `${absChange}% decrease from last month`,
        color: "#10b981",
      };
    } else {
      return {
        icon: <TrendingDown className="newdashboard-card-icon" />,
        text: "No change from last month",
        color: "#6b7280",
      };
    }
  };

  const trendDisplay = getTrendDisplay();

  return (
    <div className="newdashboard-section">
      {loading ? (
        <div className="loader-container">
          <div className="loader"></div>
          <div>Loading...</div>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="newdashboard-header-row">
            <div>
              <h2 className="newdashboard-section-title">
                Breakdown Maintenance
              </h2>
              <p className="newdashboard-subtitle">
                Monitor equipment failures and response times
              </p>
            </div>

            {/* ✅ Controlled Filters */}
            <DashboardFilters
              filters={filters}
              onFilterChange={setFilters}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
            />
          </div>

          {/* KPI Cards - Now Clickable */}
          <div className="newdashboard-kpi-grid">
            <Card
              className="newdashboard-card-clickable"
              onClick={() => handleCardClick("total")}
              style={{ cursor: "pointer" }}
            >
              <CardHeader className="newdashboard-card-header">
                <CardTitle>Total Breakdowns</CardTitle>
                <AlertTriangle className="newdashboard-card-icon" />
              </CardHeader>
              <CardContent>
                <div className="newdashboard-kpi-value">
                  {summary.total_breakdowns.count}
                </div>
                <p className="newdashboard-kpi-subtext">
                  {summary.total_breakdowns.label}
                </p>
              </CardContent>
            </Card>

            <Card
              className="newdashboard-card-clickable"
              onClick={() => handleCardClick("this_month")}
              style={{ cursor: "pointer" }}
            >
              <CardHeader className="newdashboard-card-header">
                <CardTitle>This Month</CardTitle>
                {trendDisplay.icon}
              </CardHeader>
              <CardContent>
                <div
                  className="newdashboard-kpi-value"
                  style={{ color: trendDisplay.color }}
                >
                  {summary.this_month.count}
                </div>
                <p className="newdashboard-kpi-subtext">{trendDisplay.text}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="newdashboard-card-header">
                <CardTitle>
                  {summary.most_affected_department.name || "N/A"}
                </CardTitle>
                <Wrench className="newdashboard-card-icon" />
              </CardHeader>
              <CardContent>
                <div className="newdashboard-kpi-value">
                  {summary.most_affected_department.count}
                </div>
                <p className="newdashboard-kpi-subtext">
                  {summary.most_affected_department.label}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="newdashboard-card-header">
                <CardTitle>
                  {summary.second_highest_department.name || "N/A"}
                </CardTitle>
                <Wrench className="newdashboard-card-icon" />
              </CardHeader>
              <CardContent>
                <div className="newdashboard-kpi-value">
                  {summary.second_highest_department.count}
                </div>
                <p className="newdashboard-kpi-subtext">
                  {summary.second_highest_department.label}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="newdashboard-charts-grid">
            <Card>
              <CardHeader>
                <CardTitle>Monthly Breakdown Trend</CardTitle>
                <CardDescription>Total breakdowns per month</CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer
                  className="newdashboard-chart"
                  config={{
                    breakdowns: {
                      label: "Breakdowns",
                      color: "hsl(25, 95%, 53%)",
                    },
                  }}
                >
                  <div className="newdashboard-card-content">
                    <div className="newdashboard-chart-container">
                      {charts.monthly_breakdown_trend?.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={charts.monthly_breakdown_trend}>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="#e5e7eb"
                            />
                            <XAxis
                              dataKey="month"
                              stroke="#6b7280"
                              fontSize={12}
                            />
                            <YAxis stroke="#6b7280" fontSize={12} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Bar
                              dataKey="breakdowns"
                              fill="hsl(25, 95%, 53%)"
                              name="Breakdowns"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="newdashboard-no-data">
                          No Data Found
                        </div>
                      )}
                    </div>
                  </div>
                </ChartContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Department-wise Breakdown Count</CardTitle>
                <CardDescription>
                  Total breakdowns by department
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer
                  className="newdashboard-chart"
                  config={{
                    count: { label: "Count", color: "hsl(0, 84%, 60%)" },
                  }}
                >
                  {charts.department_wise_breakdown?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={charts.department_wise_breakdown}
                        layout="vertical"
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis
                          dataKey="department"
                          type="category"
                          width={120}
                          fontSize={12}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar
                          dataKey="count"
                          fill="hsl(0, 84%, 60%)"
                          name="Breakdowns"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="newdashboard-no-data">No Data Found</div>
                  )}
                </ChartContainer>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Department Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Monthly Department-wise Breakdown</CardTitle>
              <CardDescription>
                Breakdown distribution across departments over time
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="newdashboard-card-content">
                <div className="newdashboard-chart-container">
                  {charts.monthly_department_breakdown?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={charts.monthly_department_breakdown}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                        <YAxis stroke="#6b7280" fontSize={12} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        {Object.keys(charts.monthly_department_breakdown[0])
                          .filter((key) => key !== "month")
                          .map((dept, index) => {
                            const colors = [
                              "hsl(217, 91%, 60%)",
                              "hsl(142, 71%, 45%)",
                              "hsl(262, 83%, 58%)",
                              "hsl(48, 96%, 53%)",
                              "hsl(340, 82%, 52%)",
                              "hsl(25, 95%, 53%)",
                              "hsl(220, 9%, 46%)",
                              "hsl(0, 84%, 60%)",
                              "hsl(160, 84%, 39%)",
                              "hsl(291, 64%, 42%)",
                            ];
                            return (
                              <Bar
                                key={dept}
                                dataKey={dept}
                                fill={colors[index % colors.length]}
                                name={dept}
                              />
                            );
                          })}
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="newdashboard-no-data">No Data Found</div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
